import { Component, OnInit } from '@angular/core';
import { forkJoin } from 'rxjs';
import { AuthenticationService } from 'src/app/core/services/auth.service';
import { DashboardService } from '../services/dashboard.service';
import {
  DashboardKpiGlobal,
  DashboardSociodemografico,
  DashboardAccidentes,
  DashboardPlanTrabajo,
  DashboardPlanCapacitacion,
  DashboardEnfermedadLaboral,
  DashboardCasosMedicos,
  DashboardChartItem,
  DashboardTendenciaMensual
} from './dashboard-data.model';

interface KpiCard {
  label: string;
  value: number;
  icon: string;
  color: string;
}

interface ChartOptions {
  series: any;
  chart: any;
  labels?: string[];
  colors?: string[];
  legend?: any;
  dataLabels?: any;
  responsive?: any[];
  xaxis?: any;
  stroke?: any;
  fill?: any;
  tooltip?: any;
  plotOptions?: any;
}

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit {

  breadCrumbItems: Array<{}> = [{ label: 'Inicio', active: true }];
  loading = true;
  today = new Date();
  displayName = '';
  countUpOpts = { duration: 1.5 };
  countUpOptsFast = { duration: 1 };

  kpiCards: KpiCard[] = [];

  kpiGlobal!: DashboardKpiGlobal;
  sociodem!: DashboardSociodemografico;
  accidentes!: DashboardAccidentes;
  planTrabajo!: DashboardPlanTrabajo;
  planCap!: DashboardPlanCapacitacion;
  enfLaboral!: DashboardEnfermedadLaboral;
  casosMedicos!: DashboardCasosMedicos;

  chartGenero!: ChartOptions;
  chartCargo!: ChartOptions;
  chartAccTipoEvento!: ChartOptions;
  chartAccTendencia!: ChartOptions;
  chartPtEstado!: ChartOptions;
  chartPcCursos!: ChartOptions;
  chartPcCategoria!: ChartOptions;
  chartElEstado!: ChartOptions;
  chartElTendencia!: ChartOptions;
  chartCmOrigen!: ChartOptions;
  chartCmTendencia!: ChartOptions;

  private readonly COLORS = ['#3680f4', '#2dce89', '#fb6340', '#f5365c', '#11cdef', '#8965e0', '#ffd600'];

  constructor(
    private dashboardService: DashboardService,
    public authService: AuthenticationService
  ) { }

  ngOnInit(): void {
    this.displayName = this.authService.getLoggedUser()?.displayName ?? 'Usuario';

    forkJoin({
      kpi: this.dashboardService.getKpiGlobal(),
      socio: this.dashboardService.getSociodemografico(),
      acc: this.dashboardService.getAccidentesResumen(),
      pt: this.dashboardService.getPlanTrabajoResumen(),
      pc: this.dashboardService.getPlanCapacitacionResumen(),
      el: this.dashboardService.getEnfermedadLaboralResumen(),
      cm: this.dashboardService.getCasosMedicosResumen()
    }).subscribe({
      next: ({ kpi, socio, acc, pt, pc, el, cm }) => {
        this.kpiGlobal = kpi;
        this.sociodem = socio;
        this.accidentes = acc;
        this.planTrabajo = pt;
        this.planCap = pc;
        this.enfLaboral = el;
        this.casosMedicos = cm;
        this.buildKpiCards();
        this.buildCharts();
        this.loading = false;
      },
      error: () => { this.loading = false; }
    });
  }

  private buildKpiCards(): void {
    this.kpiCards = [
      { label: 'Clientes Activos', value: this.kpiGlobal.clientesActivos, icon: 'ri-building-2-line', color: 'primary' },
      { label: 'Proyectos Activos', value: this.kpiGlobal.proyectosActivos, icon: 'ri-folder-open-line', color: 'success' },
      { label: 'Zonas Activas', value: this.kpiGlobal.zonasActivas, icon: 'ri-map-pin-line', color: 'warning' },
      { label: 'Colaboradores Activos', value: this.kpiGlobal.colaboradoresActivos, icon: 'ri-user-heart-line', color: 'info' },
      { label: 'Total Colaboradores', value: this.kpiGlobal.colaboradoresTotal, icon: 'ri-group-line', color: 'secondary' }
    ];
  }

  private buildCharts(): void {
    const s = this.sociodem;
    const a = this.accidentes;
    const pt = this.planTrabajo;
    const pc = this.planCap;
    const el = this.enfLaboral;
    const cm = this.casosMedicos;

    this.chartGenero = this.donut(s.porGenero, 210);
    this.chartCargo = this.donut(s.porCargo.slice(0, 7), 210);
    this.chartAccTipoEvento = this.donut(a.porTipoEvento, 200);
    this.chartAccTendencia = this.line(a.tendenciaMensual, 'Accidentes', '#f5365c', 185);
    this.chartPtEstado = this.donut(pt.porEstadoActividad, 200);
    this.chartPcCursos = this.donut(pc.cursosPeriodicosEstado, 200);
    this.chartPcCategoria = this.bar(pc.cumplimientoPorCategoria, 185);
    this.chartElEstado = this.donut(el.porEstado, 200);
    this.chartElTendencia = this.line(el.tendenciaMensual, 'Enfermedades', '#8965e0', 185);
    this.chartCmOrigen = this.donut(cm.porOrigenCaso, 200);
    this.chartCmTendencia = this.area(cm.tendenciaDiasIncapacidad, 'Días Incapacidad', '#11cdef', 185);
  }

  private donut(items: DashboardChartItem[], height: number): ChartOptions {
    return {
      series: items.map(i => i.value),
      chart: { type: 'donut', height, toolbar: { show: false } },
      labels: items.map(i => i.label),
      colors: this.COLORS,
      legend: { position: 'bottom', fontSize: '11px', itemMargin: { horizontal: 5 } },
      dataLabels: { enabled: false },
      responsive: [{ breakpoint: 600, options: { chart: { height: 170 }, legend: { show: false } } }]
    };
  }

  private line(items: DashboardTendenciaMensual[], name: string, color: string, height: number): ChartOptions {
    return {
      series: [{ name, data: items.map(i => i.valor) }],
      chart: { type: 'line', height, toolbar: { show: false } },
      xaxis: { categories: items.map(i => i.mes), labels: { style: { fontSize: '9px' } } },
      colors: [color],
      stroke: { curve: 'smooth', width: 2 },
      dataLabels: { enabled: false },
      tooltip: { y: { formatter: (v: number) => v.toString() } }
    };
  }

  private area(items: DashboardTendenciaMensual[], name: string, color: string, height: number): ChartOptions {
    return {
      series: [{ name, data: items.map(i => i.valor) }],
      chart: { type: 'area', height, toolbar: { show: false } },
      xaxis: { categories: items.map(i => i.mes), labels: { style: { fontSize: '9px' } } },
      colors: [color],
      stroke: { curve: 'smooth', width: 2 },
      fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.05 } },
      dataLabels: { enabled: false }
    };
  }

  private bar(items: DashboardChartItem[], height: number): ChartOptions {
    return {
      series: [{ name: 'Cumplimiento %', data: items.map(i => i.value) }],
      chart: { type: 'bar', height, toolbar: { show: false } },
      xaxis: { categories: items.map(i => i.label), labels: { style: { fontSize: '9px' } } },
      colors: ['#2dce89'],
      dataLabels: { enabled: false },
      plotOptions: { bar: { borderRadius: 4, columnWidth: '55%' } }
    };
  }
}
